import Button from 'app/components/Button'
import React, {Dispatch, SetStateAction} from 'react'

type OptionType = {
  label: string
  value: string
}

interface FilterDropdownProps {
  isOpen: boolean
  activeFilterCount: number
  toggleDropdown: () => void
  selectedResource: OptionType | null
  setSelectedResource: Dispatch<SetStateAction<OptionType | null>>
  selectedStatus: OptionType | null
  setSelectedStatus: Dispatch<SetStateAction<OptionType | null>>
  decisionMadeBy: OptionType | null
  setDecisionMadeBy: Dispatch<SetStateAction<OptionType | null>>
  personnels: any[]
  handleResetClick: () => void
  filterData: () => void
  dropdownRef: React.RefObject<HTMLDivElement>
  resources: OptionType[]
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  isOpen,
  activeFilterCount,
  toggleDropdown,
  selectedResource,
  setSelectedResource,
  selectedStatus,
  setSelectedStatus,
  decisionMadeBy,
  setDecisionMadeBy,
  personnels,
  handleResetClick,
  filterData,
  dropdownRef,
  resources,
}) => {
  // Common styles and hover handlers
  const primaryButtonStyle = {
    backgroundColor: '#0E9F6E',
    color: '#fff',
    transition: 'background-color 0.3s',
  }

  const hoverHandlers = {
    primaryEnter: (e: React.MouseEvent<HTMLElement>) =>
      (e.currentTarget.style.backgroundColor = '#148363'),
    primaryLeave: (e: React.MouseEvent<HTMLElement>) =>
      (e.currentTarget.style.backgroundColor = '#0E9F6E'),
  }

  return (
    <div className='position-relative' ref={dropdownRef}>
      <Button
        className='me-4'
        onClick={toggleDropdown}
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          className='icon icon-tabler icons-tabler-outline icon-tabler-filter mx-1 mb-1'
        >
          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
          <path d='M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z' />
        </svg>
        Filter
        {activeFilterCount > 0 && (
          <span
            className='badge badge-circle ms-2'
            style={{
              color: '#1F2A37',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              backgroundColor: '#E5E7EB',
            }}
          >
            {activeFilterCount}
          </span>
        )}
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          className={`icon icon-tabler icons-tabler-outline icon-tabler-chevron-down ms-2 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
          <path d='M6 9l6 6l6 -6' />
        </svg>
      </Button>

      {isOpen && (
        <div
          className='menu menu-sub menu-sub-dropdown w-300px w-md-325px'
          data-kt-menu='true'
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1000,
            display: 'block',
            backgroundColor: '#fff',
            borderRadius: '0.475rem',
            boxShadow: '0 0 50px 0 rgba(82,63,105,.15)',
            border: '1px solid #ebedf3',
          }}
        >
          <div className='px-7 py-5'>
            <div className='fs-5 text-dark fw-bolder'>Filter Options</div>
          </div>
          <div className='separator border-gray-200'></div>
          <div className='px-7 py-5' data-kt-user-table-filter='form'>
            {/* Resource filter */}
            <div className='mb-3'>
              <label className='form-label fs-6'>Resource:</label>
              <div className='mb-3 d-flex align-items-center'>
                <select
                  id='resource'
                  className='form-select form-control'
                  aria-label='Select Resource'
                  value={selectedResource?.value ?? ''}
                  onChange={(e) =>
                    setSelectedResource({
                      label: e.target.textContent as string,
                      value: e.target.value as string,
                    })
                  }
                >
                  <option value='' disabled selected>
                    Select Resource
                  </option>
                  {resources.map((resource) => (
                    <option key={resource.value} value={resource.value}>
                      {resource.label}
                    </option>
                  ))}
                </select>

                {selectedResource && (
                  <button
                    className='btn btn-link p-0'
                    onClick={(e) => {
                      setSelectedResource(null)
                      e.stopPropagation()
                    }}
                  >
                    <span aria-hidden='true' className='mx-2 fw-bold fs-2'>
                      &times;
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Status filter */}
            <div className='mb-3'>
              <label className='form-label fs-6'>Status</label>
              <div className='mb-3 d-flex align-items-center'>
                <select
                  id='status'
                  className='form-select form-control'
                  aria-label='Select Status'
                  value={selectedStatus?.value ?? ''}
                  onChange={(e) =>
                    setSelectedStatus({
                      label: e.target.textContent as string,
                      value: e.target.value,
                    })
                  }
                >
                  <option value='' disabled selected>
                    Select Status
                  </option>
                  <option value='approved'>Approved</option>
                  <option value='rejected'>Rejected</option>
                  <option value='needs decision'>Needs Decision</option>
                </select>

                {selectedStatus && (
                  <button
                    className='btn btn-link p-0'
                    onClick={(e) => {
                      setSelectedStatus(null)
                      e.stopPropagation()
                    }}
                  >
                    <span aria-hidden='true' className='mx-2 fw-bold fs-2'>
                      &times;
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Decision maker filter */}
            <div className='mb-3'>
              <label htmlFor='decisionMaker' className='form-label fs-6'>
                Decision Maker
              </label>
              <div className='mb-3 d-flex align-items-center'>
                <select
                  id='decisionMaker'
                  className='form-select form-control'
                  aria-label='Select Decision Maker'
                  value={decisionMadeBy?.value ?? ''}
                  onChange={(e) =>
                    setDecisionMadeBy({
                      label: e.target.textContent as string,
                      value: e.target.value,
                    })
                  }
                >
                  <option value='' disabled selected>
                    Select Decision Maker
                  </option>
                  {personnels
                    .filter((data) => data.role === 'Admin' || data.role === 'Owner')
                    .map((personnel) => (
                      <option key={personnel.id} value={personnel.id}>
                        {personnel.full_name}
                      </option>
                    ))}
                </select>

                {decisionMadeBy && (
                  <button
                    className='btn btn-link p-0'
                    onClick={(e) => {
                      setDecisionMadeBy(null)
                      e.stopPropagation()
                    }}
                  >
                    <span aria-hidden='true' className='mx-2 fw-bold fs-2'>
                      &times;
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Filter actions */}
            <div className='d-flex justify-content-end mt-2'>
              <button
                type='button'
                className='btn btn-sm btn-light fw-bold me-4 px-6 fs-6'
                data-kt-menu-dismiss='true'
                data-kt-user-table-filter='reset'
                onClick={(e) => {
                  handleResetClick()
                  e.stopPropagation()
                }}
                style={{height: '40px'}}
              >
                Reset
              </button>
              <button
                type='button'
                onClick={filterData}
                className='btn btn-sm fw-bold px-6 fs-6'
                data-kt-menu-dismiss='true'
                data-kt-user-table-filter='filter'
                style={{
                  ...primaryButtonStyle,
                  height: '40px',
                }}
                onMouseEnter={hoverHandlers.primaryEnter}
                onMouseLeave={hoverHandlers.primaryLeave}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
